import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useSearchParams } from 'react-router-dom'
import { getProducts } from '../api/catalog'
import type { ProductListResponse } from '../api/types'
import Loading from '../components/Loading'

export default function ProductListPage() {
  const [searchParams] = useSearchParams()
  const category = searchParams.get('category') ?? undefined
  const page = Number(searchParams.get('page') ?? '1')
  const [data, setData] = useState<ProductListResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setData(null)
    getProducts(category, page)
      .then(setData)
      .catch((err: Error) => setError(err.message))
  }, [category, page])

  if (error) return <p className="error container section">{error}</p>
  if (!data) return <Loading />

  return (
    <div className="container section">
      <Helmet>
        <title>Ürünlerimiz | Helmach</title>
      </Helmet>

      <div className="section-heading">
        <h1>Ürünlerimiz</h1>
      </div>
      <div className="product-grid">
        {data.products.map((product) => (
          <Link key={product.id} to={`/urunlerimiz/${product.slug}`} className="product-card">
            <img src={product.image} alt={product.title ?? ''} />
            <h3>{product.title}</h3>
          </Link>
        ))}
      </div>

      {data.meta.last_page > 1 && (
        <nav className="pagination" aria-label="Sayfalar">
          <Link
            to={pageLink(category, data.meta.current_page - 1)}
            className="pagination-prev"
            aria-disabled={data.meta.current_page <= 1}
            onClick={(e) => data.meta.current_page <= 1 && e.preventDefault()}
          >
            &laquo; Önceki
          </Link>

          {buildPageItems(data.meta.current_page, data.meta.last_page).map((item, i) =>
            item === 'ellipsis' ? (
              <span key={`ellipsis-${i}`} className="pagination-ellipsis">
                &hellip;
              </span>
            ) : (
              <Link
                key={item}
                to={pageLink(category, item)}
                className={`pagination-page${item === data.meta.current_page ? ' active' : ''}`}
                aria-current={item === data.meta.current_page ? 'page' : undefined}
              >
                {item}
              </Link>
            ),
          )}

          <Link
            to={pageLink(category, data.meta.current_page + 1)}
            className="pagination-next"
            aria-disabled={data.meta.current_page >= data.meta.last_page}
            onClick={(e) => data.meta.current_page >= data.meta.last_page && e.preventDefault()}
          >
            Sonraki &raquo;
          </Link>
        </nav>
      )}
    </div>
  )
}

function buildPageItems(current: number, last: number): (number | 'ellipsis')[] {
  const boundaryStart = 6
  const boundaryEnd = 2
  const siblingCount = 1

  const pages = new Set<number>()
  for (let i = 1; i <= Math.min(boundaryStart, last); i++) pages.add(i)
  for (let i = Math.max(last - boundaryEnd + 1, 1); i <= last; i++) pages.add(i)
  for (let i = Math.max(current - siblingCount, 1); i <= Math.min(current + siblingCount, last); i++) pages.add(i)

  const sorted = Array.from(pages).sort((a, b) => a - b)
  const items: (number | 'ellipsis')[] = []
  let prev = 0
  for (const p of sorted) {
    if (p - prev > 1) items.push('ellipsis')
    items.push(p)
    prev = p
  }
  return items
}

function pageLink(category: string | undefined, page: number) {
  const params = new URLSearchParams()
  if (category) params.set('category', category)
  if (page > 1) params.set('page', String(page))
  const query = params.toString()
  return `/urunlerimiz${query ? `?${query}` : ''}`
}
